import { Dispatch } from "redux";
import { setProjectSearchFields } from "./reducer";
import { toast } from "react-toastify";
import Project, { IProjectQueryParam, ProjectListItem } from "models/project";
import ClientProjectFinance from "models/project-finance";
import { PaginatedResult } from "models/queryable";
import { buildQueryString } from "utils/query-string";
import { PortfolioKpis, ProjectHealth, ProjectHealthHistoryItem } from "models/project-health";

export const getProjects = async (query?: IProjectQueryParam): Promise<PaginatedResult<ProjectListItem> | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/project${buildQueryString(query)}`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.data as PaginatedResult<ProjectListItem>;

    } catch (err: any) {
        toast.error(err.message ?? "Error getting projects");
        return null;
    }
}

export const getProject = async (projectId: string): Promise<Project | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/project/${projectId}`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();

        const project = data.data as Project

        if (!project) {
            toast.error("Something went wrong project not found")
            return null;
        }

        const { budgets, tasks, transactions } = project

        if (!budgets || !tasks || !transactions) {
            toast.error("Project found but error getting relations");
            return null;
        }

        return project
    } catch (err: any) {
        toast.error(err.message ?? "Error getting projects");
        return null
    }
}

export const getProjectHealth = async (projectId: string): Promise<ProjectHealth | null> => {
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/project/${projectId}/health`, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json().catch(() => null);
            throw new Error(errBody?.message ?? "Error getting project health");
        }

        const data = await res.json();
        return data.data as ProjectHealth;
    } catch (err: any) {
        toast.error(err.message ?? "Error getting project health");
        return null;
    }
}

export const getProjectHealthHistory = async (projectId: string): Promise<ProjectHealthHistoryItem[] | null> => {
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/project/${projectId}/health/history`, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json().catch(() => null);
            throw new Error(errBody?.message ?? "Error getting project health history");
        }

        const data = await res.json();
        return data.data as ProjectHealthHistoryItem[];
    } catch (err: any) {
        toast.error(err.message ?? "Error getting project health history");
        return null;
    }
}

export const getPortfolioKpis = async (query?: IProjectQueryParam): Promise<PortfolioKpis | null> => {
    try {
        // Paging and sorting do not apply to portfolio totals
        const { page, limit, sortBy, sortDir, ...filters } = query ?? ({} as IProjectQueryParam);
        const res = await fetch(`${process.env.REACT_APP_API_URL}/project/portfolio-kpis${buildQueryString(filters)}`, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json().catch(() => null);
            throw new Error(errBody?.message ?? "Error getting portfolio KPIs");
        }

        const data = await res.json();
        return data.data as PortfolioKpis;
    } catch (err: any) {
        toast.error(err.message ?? "Error getting portfolio KPIs");
        return null;
    }
}

export const getProjectSearchFields = () => async (dispatch: Dispatch) => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/project/search-fields`;
        const res = await fetch(url, {
            credentials: 'include',
            method: 'GET',
        })

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        dispatch(setProjectSearchFields(data.data))
        return data.data
    } catch (err: any) {
        toast.error(err.message ?? "Error getting projects");
    }
}

export const downloadProjectsCsv = async (query?: IProjectQueryParam) => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/project/export${buildQueryString(query)}`;

        const res = await fetch(url, {
            credentials: "include",
            method: "GET",
        });

        if (!res.ok) {
            const errBody = await res.json().catch(() => null);
            throw new Error(errBody?.message ?? "Failed to export CSV");
        }

        // Blob → downloadable file
        const blob = await res.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = `projects_${Date.now()}.csv`;
        a.click();

        window.URL.revokeObjectURL(blobUrl);

    } catch (err: any) {
        toast.error(err.message ?? "Error downloading CSV");
    }
};

export const syncProject = async (projectId: string) => {
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/project/sync/${projectId}`, {
            credentials: 'include',
            method: 'POST'
        })

        if (!res.ok) {
            const errBody = await res.json().catch(() => null);
            throw new Error(errBody?.message ?? "Failed to sync project");
        }

        const data = await res.json()

        toast.success(data.message ?? 'Project Synced')
        return

    } catch (err: any) {
        toast.error(err.message ?? "Error downloading CSV");
    }
}

export const syncProjects = async (): Promise<boolean> => {
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/project/sync`, {
            credentials: 'include',
            method: 'POST'
        })

        if (!res.ok) {
            const errBody = await res.json().catch(() => null);
            throw new Error(errBody?.message ?? "Failed to sync projects");
        }

        toast.success('Sync started. The page will refresh automatically when it finishes.')
        return true
    } catch (err: any) {
        toast.error(err.message ?? "Error starting the sync");
        return false
    }
}

export const updateProjectFinance = async (
    projectFinance: Pick<ClientProjectFinance, 'projectId' | 'fiscalPeriod' | 'plannedAmount' | 'forecastAmount'>[]
): Promise<ClientProjectFinance[] | null> => {
    try {
        const transformedProjectFinance: Pick<ClientProjectFinance, 'projectId' | 'fiscalPeriod' | 'plannedAmount' | 'forecastAmount'>[] = projectFinance.map(p => ({
            projectId: p.projectId,
            fiscalPeriod: p.fiscalPeriod,
            plannedAmount: new Number(p.plannedAmount).valueOf(),
            forecastAmount: new Number(p.forecastAmount).valueOf()
        }))

        const res = await fetch(
            `${process.env.REACT_APP_API_URL}/project/project-finance`,
            {
                credentials: "include",
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(transformedProjectFinance),
            }
        );

        if (!res.ok) {
            let errBody: any = null;
            try {
                errBody = await res.json();
            } catch {
                // ignore JSON parse failure
            }

            throw new Error(
                errBody?.message ?? "Failed to sync project finance"
            );
        }

        const data = await res.json();

        toast.success("Project Finance has been updated");
        return data.data as ClientProjectFinance[];
    } catch (err: any) {
        toast.error(err?.message ?? "Error updating project finance");
        return null
    }
};
